import { useNavigate, useParams } from "react-router-dom";
import styled, { css } from "styled-components";
import { FC, MouseEvent, useState } from "react";
import { DateTime } from "luxon";
import { useQueryClient } from "@tanstack/react-query";

import ActionsDropdown, {
  ActionsDropdownButton,
} from "@/components/ActionsDropdown";
import { ActionsWrap, Td } from "@/components/Table";
import { ReactComponent as TrashIcon } from "@/assets/icons/trashbin.svg";
import { EventsProjectResource } from "@/types/resources";
import useAlerts from "@/hooks/useAlerts";
import { api } from "@/utils/api";
import { EventsStatusEnum } from "@/utils/enums";
import { AnimatePresence } from "framer-motion";
import EventsTemplateModal from "./EventsTemplateModal";

const Tr = styled.tr`
  cursor: pointer;
  transition: background-color ease-out 0.2s;
  &:hover {
    td {
      transition: background-color ease-out 0.2s;
      background-color: ${({ theme }) =>
        theme.colors.grayscaleExtraLight} !important;
    }
  }
`;

const StatusHolder = styled.div`
  text-transform: capitalize;
  display: flex;
  align-items: center;
`;

const StyledActionsDropdown = styled(ActionsDropdown)`
  width: 170px;
`;

const StatusDot = styled.div<{ $status?: EventsStatusEnum }>`
  width: 4px;
  height: 4px;
  border-radius: 50%;
  margin-right: 8px;
  ${({ theme, $status }) =>
    $status === EventsStatusEnum.SENT &&
    css`
      background-color: ${theme.colors.success};
    `}
  ${({ theme, $status }) =>
    $status === EventsStatusEnum.DRAFT &&
    css`
      background-color: ${theme.colors.gray};
    `}
    ${({ theme, $status }) =>
    $status === EventsStatusEnum.CANCELED &&
    css`
      background-color: ${theme.colors.error};
    `}
`;

interface Props {
  project: EventsProjectResource;
}

const EventsTableRow: FC<Props> = ({ project }) => {
  const [showTemplateModal, setShowTemplateModal] = useState(false);

  const navigate = useNavigate();

  const alerts = useAlerts();

  const queryClient = useQueryClient();

  const { teamId } = useParams();

  const handleRowClick = (id: number) => {
    navigate(`/teams/${teamId}/production/event/${id}`);
  };

  const onDelete = async (e: MouseEvent<HTMLButtonElement>) => {
    try {
      e.stopPropagation();

      const confirm = await alerts.confirm(
        `Are you sure you want to delete?`,
        project.name
      );

      if (!confirm) return;

      await api.delete<never, { id: number }>(
        `/v1/production/events-projects/${project.id}`
      );

      queryClient.invalidateQueries(["production_events_projects"], {
        exact: false,
      });

      alerts.success("Event project was successfully deleted");
    } catch (e) {
      alerts.error();
    }
  };

  const onEventCancel = async (e: MouseEvent<HTMLButtonElement>) => {
    try {
      e.stopPropagation();

      const confirm = await alerts.confirm(
        `Are you sure you want to cancel this project events?`
      );

      if (!confirm) return;

      await api.post<never, { id: number }>(
        `/v1/production/events-projects/${project.id}/cancel`
      );

      queryClient.invalidateQueries(["production_events_projects"], {
        exact: false,
      });

      alerts.success("Project events were successfully canceled");
    } catch (e) {
      alerts.error();
    }
  };

  const renderStatusTitle = () => {
    switch (project.status) {
      case EventsStatusEnum.SENT:
        return "Sent";
      case EventsStatusEnum.DRAFT:
        return "Draft";
      case EventsStatusEnum.CANCELED:
        return "Canceled";
    }
  };

  return (
    <Tr onClick={() => handleRowClick(project.id)}>
      <Td>{project.name}</Td>
      <Td>{project.compilationId ?? "-"}</Td>
      <Td>{project.channelOwner?.email ?? "-"}</Td>
      <Td>
        <StatusHolder>
          <StatusDot $status={project.status} />
          {renderStatusTitle()}
        </StatusHolder>
      </Td>
      <Td>{DateTime.fromISO(project.createdAt).toLocaleString()}</Td>
      <Td $actionsCol>
        <ActionsWrap>
          <StyledActionsDropdown>
            <ActionsDropdownButton
              as="button"
              onClick={(e: MouseEvent<HTMLButtonElement>) => {
                e.stopPropagation();
                setShowTemplateModal(true);
              }}
            >
              <span>
                <svg
                  width="20"
                  height="16"
                  viewBox="0 0 20 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M13.4231 4H1.65385C1.29274 4 1 4.29848 1 4.66667V14C1 14.3682 1.29274 14.6667 1.65385 14.6667H13.4231C13.7842 14.6667 14.0769 14.3682 14.0769 14V4.66667C14.0769 4.29848 13.7842 4 13.4231 4Z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M4 3.75V1.6875C4 1.50516 4.07375 1.3303 4.20502 1.20136C4.3363 1.07243 4.51435 1 4.7 1H17.3C17.4857 1 17.6637 1.07243 17.795 1.20136C17.9262 1.3303 18 1.50516 18 1.6875V11.3125C18 11.4948 17.9262 11.6697 17.795 11.7986C17.6637 11.9276 17.4857 12 17.3 12H15.2"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M1 8H14.0769"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
              Use as a template
            </ActionsDropdownButton>
            {project.status === EventsStatusEnum.SENT ? (
              <ActionsDropdownButton as="button" onClick={onEventCancel}>
                <span>
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M14.3854 7.99985C14.3854 11.526 11.527 14.3845 8.00083 14.3845C4.4747 14.3845 1.61621 11.526 1.61621 7.99985C1.61621 4.47372 4.4747 1.61523 8.00083 1.61523C11.527 1.61523 14.3854 4.47372 14.3854 7.99985Z"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeMiterlimit="10"
                    />
                    <path
                      d="M10 6L6 10"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M10 10L6 6"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
                Cancel events
              </ActionsDropdownButton>
            ) : (
              <ActionsDropdownButton as="button" onClick={onDelete}>
                <span>
                  <TrashIcon />
                </span>
                Delete
              </ActionsDropdownButton>
            )}
          </StyledActionsDropdown>
        </ActionsWrap>
      </Td>
      <AnimatePresence>
        {showTemplateModal && (
          <EventsTemplateModal
            projectId={project.id}
            onClose={() => setShowTemplateModal(false)}
          />
        )}
      </AnimatePresence>
    </Tr>
  );
};

export default EventsTableRow;
